import { useEffect } from 'react';

import { type PageLoadMetric } from '@atlassian/browser-metrics';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	createPageLoadMetric,
	getWaterfallTimings,
} from '@confluence/browser-metrics/entry-points/metricConfigs.ts';
import {
	APP_NAVIGATION_SEGMENT_KEY,
	SPACE_NAVIGATION_SEGMENT_KEY,
	PAGE_TREE_SEGMENT_KEY,
	SHARE_BUTTON_SEGMENT_KEY,
	LIVE_PAGE_LOAD_KEY,
	LIVE_PAGE_REACT_UFO_LOAD_KEY,
	OBJECT_HEADER_SEGMENT_KEY,
	OBJECT_SIDEBAR_CONTROL_SEGMENT_KEY,
} from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { isInitial, markBrowserMetricEnd } from '@confluence/browser-metrics/entry-points/utils.ts';
import { resumeLowPriorityEvents } from '@confluence/analytics-event-delay';
import { APP_NAVIGATION_METRIC } from '@confluence/browser-metrics/entry-points/app-navigation.metric.ts';
import { PAGE_TREE_METRIC } from '@confluence/page-tree/entry-points/pageSegmentLoadMetrics.ts';
import { SHARE_BUTTON_PAGE_SEGMENT_LOAD } from '@confluence/share-and-restrict-dialog/entry-points/pageSegmentLoadMetrics.ts';
import { SPACE_NAVIGATION_METRIC } from '@confluence/side-navigation/entry-points/pageSegmentLoadMetrics.ts';
import { OBJECT_SIDEBAR_CONTROL_METRIC } from '@confluence/object-sidebar-components/entry-points/pageSegmentLoadMetrics.ts';
import { OBJECT_HEADER_SEGMENT_METRIC } from '@confluence/content-types-header/entry-points/pageSegmentLoadMetrics.ts';
import { EDITOR_METRIC } from '@confluence/editor-features/entry-points/perf.config.ts';

import { fg } from '@confluence/feature-gating';
import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/page-segment-load';

const baseConfig = {
	histogram: {
		initial: {
			fmp: '5000_6000_7000_8000_10000_12000_14000',
			tti: '5000_6000_7000_8000_10000_12000_14000',
		},
		transition: {
			fmp: '2000_2500_3000_3500_4000_4500_5000',
			tti: '2000_2500_3000_3500_4000_4500_5000',
		},
	},
	timings: [
		// breakdowns in order of occurrence
		{ key: 'html', endMark: 'html' },
		{ key: 'appLoad', startMark: 'html', endMark: 'app' },
		{
			key: 'appRootRender',
			startMark: 'app',
			endMark: 'root-component-render',
		},
		{
			key: 'routerMatched',
			startMark: 'root-component-render',
			endMark: 'route-manager-initialized',
		},
	],
};

const NOOP = () => {};

export const getExpectedPageLoadSegments = ({
	isAnonymous,
	isInitialLoad,
	viewMode,
}: {
	isAnonymous: boolean;
	isInitialLoad: boolean;
	viewMode?: boolean;
}): PageSegmentLoadMetric[] => {
	const segments = [EDITOR_METRIC];

	if (fg('confluence_frontend_content_wrapper')) {
		segments.push(OBJECT_SIDEBAR_CONTROL_METRIC);
	}

	if (fg('confluence_frontend_content_wrapper')) {
		segments.push(OBJECT_HEADER_SEGMENT_METRIC);
	}

	if (isInitialLoad) {
		segments.push(APP_NAVIGATION_METRIC, SPACE_NAVIGATION_METRIC, PAGE_TREE_METRIC);
	}

	if (!isAnonymous && viewMode === false) {
		segments.push(SHARE_BUTTON_PAGE_SEGMENT_LOAD);
	}

	return segments;
};

/**
 * fmpAsUntilStopTimeMetrics
 *
 * UPDATE 2025-04-29: TTI is deprecated. Going forward, new segments should be included here by default in order to
 * preserve the original TTI definition. Segments should be included even if they are not minimally interactive.
 *
 * What is minimally interactive?
 * Minimally interactive during the SSR phase at it's core, is determined if the component/element is clickable,
 * can register the action, and display a visual response that the action has been received.
 * The SSR phase can be stalled by appending ?NO_SPA=1 at the end of a CONNIE url.
 *
 * I.E. If you click on an inline comment/highlight, the click is registered, and displays a loading sidebar component.
 *
 * If a required segment is SSR'd and determined to be minimally interactive at FMP, ADD TO THIS LIST
 * If a required segment is SSR'd but determined to NOT be minimally interactive at FMP, DO NOT ADD TO THIS LIST
 * If a required segment is yet to be SSR'd, this config will not update TTI for that segment, by default is OFF.
 *
 * CONTEXT
 * the returned segments are considered to be minimally interactive during SSR
 * hence we want to use FMP (ssr-ttr mark)
 *
 * PageSegment TTI is measured: useFmpAsUntilStopTime ? FMP : Eventual load time
 * Content-renderer TTI: macros are SSRed ? FMP : Eventual Load Time
 *
 * View Page TTI is measured: Math.max(Non SSR PageSegments TTI, Content-renderer TTI)
 *
 */
const fmpAsUntilStopTimeMetrics = (): string[] => {
	return [
		APP_NAVIGATION_SEGMENT_KEY,
		SPACE_NAVIGATION_SEGMENT_KEY,
		PAGE_TREE_SEGMENT_KEY,
		OBJECT_HEADER_SEGMENT_KEY,
		OBJECT_SIDEBAR_CONTROL_SEGMENT_KEY,
		SHARE_BUTTON_SEGMENT_KEY,
	];
};

// This component assumes startBrowserMetricsPageLoad() has already been called from
// packages/confluence-frontend-server/src/components/Root/App.js in order to generate
// accurate pageLoad start timings. This function is supplementary and starts the
// `until` listeners which will automatically stop the pageLoad.
interface StartLivePageLoadParams {
	contentId: string;
	viewMode: boolean;
}

interface StartLivePageLoadMetricsParams {
	viewMode?: boolean;
	userId?: string;
}

/**
 * Starts live page load metrics tracking and returns a cleanup function.
 * This function can be used outside of React components.
 */
export const startLivePageLoadMetrics = ({
	viewMode,
	userId,
}: StartLivePageLoadMetricsParams): PageLoadMetric => {
	const _isInitialLoad = isInitial();
	const expectedPageSegments = getExpectedPageLoadSegments({
		isAnonymous: Boolean(!userId),
		isInitialLoad: _isInitialLoad,
		viewMode,
	});

	const untilExperienceMetricNames = fmpAsUntilStopTimeMetrics();

	// Create a list of untilPageSegments
	// This wraps the segments into an UntilExperience, so that @atlassian/browser-metrics can handle useFmpAsUntilStopTime gracefully
	// Note - this only affect initial load since transitions are not SSR'd, hence page-segments do not have fmp marks
	// More information why useFmpAsUntilStopTime can be found here
	// https://hello.atlassian.net/wiki/spaces/~484600559/pages/1515596554/Toolings+Update+Browser-Metrics+FMP+TTI
	const untilPageSegments = expectedPageSegments.map((segment) => ({
		experience: segment,
		useFmpAsUntilStopTime: untilExperienceMetricNames.includes(segment.key),
	}));

	const pageLoadMetric = createPageLoadMetric({
		...baseConfig,
		key: LIVE_PAGE_LOAD_KEY,
		timings: [...baseConfig.timings, ...getWaterfallTimings(expectedPageSegments)],
		include: expectedPageSegments,
		until: untilPageSegments,
		reactUFOName: LIVE_PAGE_REACT_UFO_LOAD_KEY,
	});

	if (!_isInitialLoad) {
		// Make sure all segments are re-started and not currently marked as finished from a previous navigation
		expectedPageSegments.forEach((m) => m.startFromPageLoad());
	}

	pageLoadMetric.startPageLoad();
	pageLoadMetric.onStop(() => {
		markBrowserMetricEnd(pageLoadMetric);
	}, NOOP);
	pageLoadMetric.onStop(resumeLowPriorityEvents, resumeLowPriorityEvents);

	return pageLoadMetric;
};

export const StartLivePageLoad = ({ contentId, viewMode }: StartLivePageLoadParams) => {
	const { userId } = useSessionData();

	useEffect(() => {
		const pageLoadMetric = startLivePageLoadMetrics({
			viewMode,
			userId: userId ?? undefined,
		});

		return () => {
			pageLoadMetric?.cancel();
		};
	}, [contentId, userId, viewMode]);

	return null;
};
